import React, { useState } from 'react';
import { ChevronDown, ChevronRight, GripVertical, X, Edit3 } from 'lucide-react';
import { toast } from 'sonner';
import { IconContainer } from '../../../components/brand/BrandIcon';
import { SmallText } from '../../../components/brand/BrandTypography';
import { PrimaryButton, SecondaryButton, TertiaryButton } from '../../../components/brand/BrandButton';
import { TOOL_TYPE_CONFIG, PATH_ICONS, API_URL } from '../constants';

const PathEditor = ({ path, tools, onUpdate }) => {
    const [expanded, setExpanded] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editData, setEditData] = useState(path);
    const PathIcon = PATH_ICONS[path.icon] || PATH_ICONS.Snowflake;

    const handleSave = async () => {
        try {
            const response = await fetch(`${API_URL}/api/tools/paths/${path.path_id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(editData)
            });
            if (response.ok) {
                onUpdate();
                setEditing(false);
                toast.success('Path updated');
            }
        } catch (error) {
            toast.error('Failed to update path');
        }
    };

    const addStep = (toolId) => {
        const newSteps = [...(editData.steps || []), { tool_id: toolId, order: editData.steps?.length || 0 }];
        setEditData({ ...editData, steps: newSteps });
    };

    const removeStep = (index) => {
        const newSteps = editData.steps.filter((_, i) => i !== index);
        setEditData({ ...editData, steps: newSteps.map((s, i) => ({ ...s, order: i })) });
    };

    const moveStep = (index, direction) => {
        const newSteps = [...editData.steps];
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= newSteps.length) return;
        [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
        setEditData({ ...editData, steps: newSteps.map((s, i) => ({ ...s, order: i })) });
    };

    return (
        <div className="bg-white rounded-xl border border-[#FFBB98] overflow-hidden">
            <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-[#FEF6EF]"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-center gap-3">
                    <IconContainer variant={path.variant} size="md">
                        <PathIcon className="w-5 h-5" />
                    </IconContainer>
                    <div>
                        <h3 className="font-medium text-[#1D6B6B]">{path.name}</h3>
                        <p className="text-sm text-[#7D8E95]">{path.description}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-sm text-[#7D8E95]">{path.steps?.length || 0} steps</span>
                    {expanded ? <ChevronDown className="w-5 h-5 text-[#7D8E95]" /> : <ChevronRight className="w-5 h-5 text-[#7D8E95]" />}
                </div>
            </div>

            {expanded && (
                <div className="border-t border-[#FFBB98] p-4">
                    {editing ? (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <input
                                    type="text"
                                    value={editData.name}
                                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                                    className="px-3 py-2 rounded-lg border border-[#FFBB98] text-sm"
                                    placeholder="Path name"
                                />
                                <input
                                    type="text"
                                    value={editData.description}
                                    onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                                    className="px-3 py-2 rounded-lg border border-[#FFBB98] text-sm"
                                    placeholder="Description"
                                />
                            </div>

                            <div className="space-y-2">
                                <SmallText className="font-medium text-[#1D6B6B]">Steps:</SmallText>
                                {editData.steps?.map((step, index) => {
                                    const tool = tools.find(t => t.tool_id === step.tool_id);
                                    const config = TOOL_TYPE_CONFIG[tool?.type] || TOOL_TYPE_CONFIG.breath;
                                    return (
                                        <div key={index} className="flex items-center gap-2 bg-[#FEF6EF] rounded-lg p-2">
                                            <GripVertical className="w-4 h-4 text-[#7D8E95]" />
                                            <span className="text-sm flex-1">{tool?.name || step.tool_id}</span>
                                            <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${config.bgColor}40`, color: config.color }}>
                                                {config.label}
                                            </span>
                                            <button onClick={() => moveStep(index, -1)} className="p-1 hover:bg-white rounded">↑</button>
                                            <button onClick={() => moveStep(index, 1)} className="p-1 hover:bg-white rounded">↓</button>
                                            <button onClick={() => removeStep(index)} className="p-1 hover:bg-red-100 rounded text-red-500">
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    );
                                })}

                                <select
                                    onChange={(e) => { if (e.target.value) addStep(e.target.value); e.target.value = ''; }}
                                    className="w-full px-3 py-2 rounded-lg border border-dashed border-[#7D8E95] text-sm text-[#1D6B6B] bg-transparent"
                                >
                                    <option value="">+ Add step...</option>
                                    {tools.map(tool => (
                                        <option key={tool.tool_id} value={tool.tool_id}>{tool.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex gap-2">
                                <TertiaryButton onClick={() => { setEditing(false); setEditData(path); }} className="flex-1">
                                    Cancel
                                </TertiaryButton>
                                <PrimaryButton onClick={handleSave} className="flex-1">
                                    Save Path
                                </PrimaryButton>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className="space-y-2 mb-4">
                                {path.steps?.map((step, index) => {
                                    const tool = tools.find(t => t.tool_id === step.tool_id);
                                    const config = TOOL_TYPE_CONFIG[tool?.type] || TOOL_TYPE_CONFIG.breath;
                                    return (
                                        <div key={index} className="flex items-center gap-2 text-sm">
                                            <span className="w-6 h-6 rounded-full bg-transparent flex items-center justify-center text-[#7D8E95]">
                                                {index + 1}
                                            </span>
                                            <span className="flex-1">{tool?.name || step.tool_id}</span>
                                            <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: `${config.bgColor}40`, color: config.color }}>
                                                {config.label}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            <SecondaryButton onClick={() => setEditing(true)} className="w-full">
                                <Edit3 className="w-4 h-4 mr-2" />
                                Edit Path
                            </SecondaryButton>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PathEditor;
